import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError, money, fmtDate } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { AlertTriangle, Layers } from "lucide-react";
import ClienteEditDialog, { camposFaltantesDaMensagem } from "./ClienteEditDialog";

const MAX_PARCELAS = 12;

// Prévia client-side — o backend RECALCULA tudo em centavos (fonte da
// verdade). Aqui é só para o usuário conferir antes de confirmar.
function splitCents(totalCents, n) {
  const base = Math.floor(totalCents / n);
  const resto = totalCents - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < resto ? 1 : 0));
}
function addMonthsClamp(iso, months) {
  const [y, m, d] = iso.split("-").map(Number);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const lastDay = new Date(ny, nm, 0).getDate();
  const nd = Math.min(d, lastDay);
  return `${ny}-${String(nm).padStart(2, "0")}-${String(nd).padStart(2, "0")}`;
}
// Etapa 2 — prévia client-side do parcelamento por VALOR-BASE (mesma regra
// de _split_by_base_amount do backend, só para EXIBIÇÃO — o backend
// recalcula/valida tudo de novo, nunca confia nesta prévia).
function splitByBaseCents(totalCents, baseCents) {
  if (totalCents <= 0 || baseCents <= 0 || baseCents > totalCents) return [];
  const nFull = Math.floor(totalCents / baseCents);
  const resto = totalCents - nFull * baseCents;
  const valores = Array.from({ length: nFull }, () => baseCents);
  if (resto > 0) valores.push(resto);
  return valores;
}

export default function AglutinarDialog({ open, onOpenChange, clienteNome, clienteId, tickets, onCreated,
                                          temPixIntegrado = false, temPixChave = false }) {
  const [forma, setForma] = useState("avista");
  const [parcelas, setParcelas] = useState(3);
  const [primeiro, setPrimeiro] = useState(new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10));
  const [vencs, setVencs] = useState([]);
  const [venc1, setVenc1] = useState(new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  // Edição CONTEXTUAL do cadastro do cliente. `clienteEdit` guarda só a
  // mensagem do backend (para destacar o campo que falta); ele é montado
  // como IRMÃO deste conteúdo, então este componente nunca desmonta e todo
  // o estado já preenchido (forma, PIX, boleto, parcelas, vencimentos,
  // entrada) continua intacto por construção — nada é serializado nem
  // reconstruído.
  const [clienteEdit, setClienteEdit] = useState(null);
  // BUG DE HOMOLOGAÇÃO: a ação "Editar cliente" aparecia só no toast do
  // sonner, cujo <Toaster> vive em App.js — FORA do portal deste Dialog.
  // O Radix Dialog é modal: aplica `pointer-events: none` no <body> e
  // aria-hidden em tudo que está fora do seu conteúdo, então o botão do
  // toast ficava VISÍVEL mas nunca recebia o clique. A ação principal
  // passa a ser um alerta INLINE, dentro do próprio DialogContent (sempre
  // clicável e acessível); o toast continua como aviso secundário, sem
  // botão morto.
  const [cadastroPendente, setCadastroPendente] = useState(null);

  // Etapa 2 — modo de parcelamento: "quantidade" (Dividir igualmente, já
  // existia) ou "valor" (Informar valor das parcelas — novo). Dentro de
  // "valor": repetirValor null = ainda não respondeu Sim/Não;
  // true = valor-base (repete enquanto houver saldo, backend calcula a
  // quantidade); false = manual (item 6: quantidade perguntada de cara,
  // gera N linhas em parcelasManuaisLinhas, cada uma com valor+vencimento).
  const [modoParcelamento, setModoParcelamento] = useState("quantidade");
  const [primeiraParcela, setPrimeiraParcela] = useState("300");
  const [repetirValor, setRepetirValor] = useState(null);
  // Pacote Faturamento/PIX/Fichas, item 6 — Caso A (repetir=Sim): datas por
  // parcela, editáveis individualmente (mesmo padrão do modo "quantidade").
  const [vencsValorBase, setVencsValorBase] = useState([]);
  // Item 6 — Caso B (repetir=Não): pergunta a quantidade de parcelas de
  // cara e gera N linhas já editáveis (número/valor/vencimento), substitui
  // o antigo fluxo de "adicionar uma parcela por vez".
  const [parcelasManuaisQtd, setParcelasManuaisQtd] = useState(2);
  const [parcelasManuaisLinhas, setParcelasManuaisLinhas] = useState([]); // [{valor, vencimento}]

  // Etapa 3 — ENTRADA + parcelamento do SALDO. Só relevante dentro de
  // "parcelado" (entradaAtiva já reflete isso) — os 3 modos acima passam
  // a operar sobre o SALDO (baseParcelamentoCents), nunca sobre o total
  // cheio, sem nenhuma mudança na mecânica de cada modo em si.
  const [possuiEntrada, setPossuiEntrada] = useState(false);
  const [entradaValor, setEntradaValor] = useState("500");
  const [entradaVencimento, setEntradaVencimento] = useState(new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10));
  // Últimos ajustes antes da produção, item 2 — a entrada pode ser PIX
  // (integrado ou por chave) em vez de boleto (padrão histórico). Quando
  // só UMA modalidade de PIX está configurada, ela é assumida
  // silenciosamente (mesmo padrão de abrirGerarPix em Faturamento.jsx —
  // só pergunta quando as duas existem); com nenhuma configurada, "PIX"
  // nem aparece como opção de instrumento da entrada.
  const [entradaInstrumento, setEntradaInstrumento] = useState("boleto"); // "boleto" | "pix"
  const [entradaPixModalidade, setEntradaPixModalidade] = useState(null); // "integrado" | "chave" | null

  // Delta Parcelamento via PIX — "Forma da cobrança" do PARCELAMENTO em
  // si (as N parcelas do saldo — MESMO editor de quantidade/valor-base/
  // manual reaproveitado abaixo, nenhuma lógica financeira nova).
  // Independente do instrumento da ENTRADA acima — compõem livremente.
  const [parcelamentoInstrumento, setParcelamentoInstrumento] = useState("boleto"); // "boleto" | "pix"
  const [parcelamentoPixModalidade, setParcelamentoPixModalidade] = useState(null); // "integrado" | "chave" | null

  // Etapa 4D — PAGAMENTO MISTO (item 2 do pedido). `tipoComposicao`
  // decide entre o fluxo histórico ("unica": tudo acima, intocado) e o
  // novo ("misto": exatamente 1 PIX + 1 BOLETO — contrato já implementado
  // no backend desde a Etapa 4B). O BOLETO do misto reutiliza os MESMOS
  // 3 modos de parcelamento (modoParcelamento/repetirValor/... acima) —
  // só a "base" sobre a qual eles operam muda (ver parcelamentoBaseCents
  // abaixo); nenhuma lógica financeira nova. Entrada nunca combina com
  // misto (item 4) — os dois fluxos são mutuamente exclusivos por
  // `tipoComposicao`.
  const [tipoComposicao, setTipoComposicao] = useState("unica"); // "unica" | "misto"
  const [pixValor, setPixValor] = useState("");
  const [pixVencimento, setPixVencimento] = useState(new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10));
  // Últimos ajustes antes da produção, item 2 — o componente PIX do misto
  // é sempre PIX (nunca escolha de instrumento, já era assim); a
  // modalidade (integrado x chave) agora é explícita, mesmo padrão de
  // entradaPixModalidade acima.
  const [pixModalidade, setPixModalidade] = useState(null); // "integrado" | "chave" | null
  const [boletoParcelado, setBoletoParcelado] = useState(null); // null (sem resposta) | true | false
  const [boletoVencimentoUnico, setBoletoVencimentoUnico] = useState(new Date(Date.now() + 20 * 86400000).toISOString().slice(0, 10));

  const totalCents = useMemo(
    () => tickets.reduce((a, t) => a + Math.round((t.valor_total || 0) * 100), 0),
    [tickets],
  );
  const total = totalCents / 100;

  const misto = tipoComposicao === "misto";
  const entradaAtiva = !misto && forma === "parcelado" && possuiEntrada;
  const entradaCents = entradaAtiva ? Math.round((Number(entradaValor) || 0) * 100) : 0;
  const baseParcelamentoCents = entradaAtiva ? Math.max(0, totalCents - entradaCents) : totalCents;

  // PIX/BOLETO do misto (item 2) — o boleto é sempre "o que sobra do PIX",
  // nunca exige o usuário digitar os dois valores.
  const pixCents = misto ? Math.round((Number(pixValor) || 0) * 100) : 0;
  const boletoCents = misto ? Math.max(0, totalCents - pixCents) : 0;
  const mistoSomaCents = pixCents + boletoCents;
  const mistoDiferencaCents = totalCents - mistoSomaCents;

  // "base" sobre a qual o editor de parcelamento (quantidade/valor-base/
  // manual) reutilizado abaixo opera: saldo pós-entrada no fluxo único,
  // ou o valor do componente BOLETO no misto. `parcelamentoAtivo` marca
  // quando esse editor está em uso em QUALQUER um dos dois fluxos.
  const parcelamentoBaseCents = misto ? boletoCents : baseParcelamentoCents;
  const parcelamentoAtivo = (!misto && forma === "parcelado") || (misto && boletoParcelado === true);

  // regenera a grade quando muda qtde/primeiro vencimento (mantém edições
  // manuais só enquanto o tamanho não muda)
  useEffect(() => {
    if (!parcelamentoAtivo || modoParcelamento !== "quantidade") return;
    const n = Math.max(2, Math.min(MAX_PARCELAS, Number(parcelas) || 2));
    setVencs(Array.from({ length: n }, (_, i) => addMonthsClamp(primeiro, i)));
  }, [parcelamentoAtivo, modoParcelamento, parcelas, primeiro]);

  useEffect(() => {
    if (open) {
      setForma("avista"); setParcelas(3);
      setPrimeiro(new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10));
      setVenc1(new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10));
      setModoParcelamento("quantidade"); setPrimeiraParcela("300");
      setRepetirValor(null); setVencsValorBase([]);
      setParcelasManuaisQtd(2); setParcelasManuaisLinhas([]);
      setPossuiEntrada(false); setEntradaValor("500");
      setEntradaVencimento(new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10));
      setEntradaInstrumento("boleto"); setEntradaPixModalidade(null);
      setParcelamentoInstrumento("boleto"); setParcelamentoPixModalidade(null);
      setTipoComposicao("unica"); setPixValor("");
      setPixVencimento(new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10));
      setPixModalidade(null);
      setBoletoParcelado(null);
      setBoletoVencimentoUnico(new Date(Date.now() + 20 * 86400000).toISOString().slice(0, 10));
    }
  }, [open]);

  // Últimos ajustes antes da produção, item 2 — só UMA modalidade de PIX
  // configurada: assume-a automaticamente (nunca força uma escolha
  // desnecessária), tanto para a entrada quanto para o componente PIX do
  // misto. Com as DUAS configuradas, o usuário escolhe explicitamente
  // (fica null até isso acontecer — bloqueia "Gerar fatura" enquanto
  // ambíguo, mesma lógica de planoValido/mistoPlanoValido abaixo).
  const modalidadeUnicaDisponivel = temPixIntegrado && !temPixChave ? "integrado"
    : temPixChave && !temPixIntegrado ? "chave" : null;
  useEffect(() => {
    if (entradaInstrumento === "pix" && !entradaPixModalidade && modalidadeUnicaDisponivel) {
      setEntradaPixModalidade(modalidadeUnicaDisponivel);
    }
  }, [entradaInstrumento, entradaPixModalidade, modalidadeUnicaDisponivel]);
  useEffect(() => {
    if (misto && !pixModalidade && modalidadeUnicaDisponivel) {
      setPixModalidade(modalidadeUnicaDisponivel);
    }
  }, [misto, pixModalidade, modalidadeUnicaDisponivel]);
  useEffect(() => {
    if (parcelamentoInstrumento === "pix" && !parcelamentoPixModalidade && modalidadeUnicaDisponivel) {
      setParcelamentoPixModalidade(modalidadeUnicaDisponivel);
    }
  }, [parcelamentoInstrumento, parcelamentoPixModalidade, modalidadeUnicaDisponivel]);

  const valoresPreview = parcelamentoAtivo && modoParcelamento === "quantidade" ? splitCents(parcelamentoBaseCents, vencs.length || 1) : [];

  const primeiraParcelaCents = Math.round((Number(primeiraParcela) || 0) * 100);
  const valorBasePreview = splitByBaseCents(parcelamentoBaseCents, primeiraParcelaCents);
  const parcelasManuaisCents = parcelasManuaisLinhas.map(r => Math.round((Number(r.valor) || 0) * 100));
  const distribuidoCents = parcelasManuaisCents.reduce((a, c) => a + c, 0);
  const restanteCents = parcelamentoBaseCents - distribuidoCents;

  // Item 6, Caso A — regenera as N datas (uma por parcela) sempre que a
  // quantidade (derivada do valor-base/saldo) ou o 1º vencimento mudam;
  // edições individuais de uma data não disparam isto de volta (mesmo
  // padrão do efeito equivalente do modo "quantidade" acima).
  useEffect(() => {
    if (!parcelamentoAtivo || modoParcelamento !== "valor" || repetirValor !== true) return;
    const n = valorBasePreview.length;
    setVencsValorBase(n > 0 && primeiro ? Array.from({ length: n }, (_, i) => addMonthsClamp(primeiro, i)) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelamentoAtivo, modoParcelamento, repetirValor, valorBasePreview.length, primeiro]);

  // Item 6, Caso B — gera/ajusta as N linhas quando a quantidade informada
  // muda, preservando valor/vencimento já digitados nas linhas existentes.
  useEffect(() => {
    if (!parcelamentoAtivo || modoParcelamento !== "valor" || repetirValor !== false) return;
    const n = Math.max(2, Math.min(MAX_PARCELAS, Number(parcelasManuaisQtd) || 2));
    setParcelasManuaisLinhas(rows => Array.from({ length: n }, (_, i) => ({
      valor: rows[i]?.valor ?? "",
      vencimento: rows[i]?.vencimento ?? (primeiro ? addMonthsClamp(primeiro, i) : ""),
    })));
  }, [parcelamentoAtivo, modoParcelamento, repetirValor, parcelasManuaisQtd, primeiro]);

  const setParcelaManualCampo = (i, campo, valor) =>
    setParcelasManuaisLinhas(rows => rows.map((r, j) => j === i ? { ...r, [campo]: valor } : r));

  // Etapa 3 — resumo unificado (entrada + parcelas do saldo, qualquer que
  // seja o modo) — item 23 do pedido. Item 6 — datas de valor_base/manual
  // agora vêm das linhas editáveis (vencsValorBase/parcelasManuaisLinhas),
  // não mais de uma projeção mensal única a partir de "primeiro".
  const saldoValoresCents = modoParcelamento === "quantidade" ? valoresPreview
    : repetirValor === true ? valorBasePreview
      : repetirValor === false ? parcelasManuaisCents
        : [];
  const saldoVencimentosPreview = modoParcelamento === "quantidade" ? vencs
    : repetirValor === true ? vencsValorBase
      : repetirValor === false ? parcelasManuaisLinhas.map(r => r.vencimento)
        : [];
  // Item 8 — Resumo simplificado: só a QUANTIDADE de parcelas do saldo,
  // nunca a lista item a item (essa fica no editor à direita, item 7).
  const qtdParcelasSaldo = saldoValoresCents.length;
  const composicaoTotalCents = entradaCents + saldoValoresCents.reduce((a, c) => a + c, 0);
  const composicaoDiferencaCents = totalCents - composicaoTotalCents;

  // Item 6 — a "primeira data do saldo" agora vem das linhas editáveis nos
  // casos A/B também (antes era sempre `primeiro`, uma projeção única).
  const primeiraDataSaldo = modoParcelamento === "quantidade" ? (vencs[0] || null)
    : repetirValor === true ? (vencsValorBase[0] || null)
      : repetirValor === false ? (parcelasManuaisLinhas[0]?.vencimento || null)
        : null;
  const entradaValida = !entradaAtiva || (entradaCents > 0 && entradaCents < totalCents && !!entradaVencimento);
  const cronologiaValida = !entradaAtiva || !primeiraDataSaldo || primeiraDataSaldo > entradaVencimento;
  // Últimos ajustes antes da produção, item 2 — entrada em PIX exige
  // modalidade escolhida (nunca avança ambíguo entre integrado/chave).
  const entradaInstrumentoValido = !entradaAtiva || entradaInstrumento !== "pix" || !!entradaPixModalidade;
  // Delta Parcelamento via PIX — mesma regra para o parcelamento em si.
  const parcelamentoInstrumentoValido = forma !== "parcelado" || parcelamentoInstrumento !== "pix" || !!parcelamentoPixModalidade;

  // Item 15 do pedido — esta validação é só UX (desabilita o botão); o
  // backend SEMPRE recalcula/valida tudo de novo, nunca confia nela. Já
  // usa `parcelamentoBaseCents`, então funciona sem alteração tanto para o
  // saldo pós-entrada (fluxo único) quanto para o componente BOLETO
  // (misto) — reutilizada por `mistoBoletoValido` abaixo (item 3: "não
  // duplicar lógica financeira no frontend"). Item 6 — Casos A/B agora
  // exigem TODAS as datas por parcela preenchidas (não mais um único
  // "primeiro vencimento" compartilhado).
  const parcelamentoSubModoValido =
    modoParcelamento === "quantidade" ? vencs.length >= 2
      : repetirValor === true ? (primeiraParcelaCents > 0 && primeiraParcelaCents <= parcelamentoBaseCents
          && vencsValorBase.length > 0 && vencsValorBase.every(v => !!v))
      : repetirValor === false ? (restanteCents === 0 && parcelasManuaisCents.every(v => v > 0)
          && parcelasManuaisLinhas.every(r => !!r.vencimento))
      : false;

  const planoValido = forma === "avista" || (entradaValida && cronologiaValida && parcelamentoSubModoValido && entradaInstrumentoValido && parcelamentoInstrumentoValido);

  // Etapa 4D (item 2) — confirmar só com PIX>0, boleto>0 e soma exata; e
  // com o próprio BOLETO fechado (único com vencimento, ou parcelado com
  // o mesmo editor/validação reutilizados acima).
  const mistoValido = pixCents > 0 && !!pixVencimento && boletoCents > 0 && mistoDiferencaCents === 0 && !!pixModalidade;
  const mistoBoletoValido = boletoParcelado === false ? !!boletoVencimentoUnico
    : boletoParcelado === true ? parcelamentoSubModoValido
    : false;
  const mistoPlanoValido = mistoValido && mistoBoletoValido;

  // Ajuste de homologação — Ciclo correto da cobrança, item 3, cenário A:
  // pagamento misto é uma das composições escolhidas NA CRIAÇÃO da fatura
  // (à vista/parcelado com entrada em boleto continuam sem instrumento
  // até depois — cenário B, validado em Faturamento.jsx na hora da
  // emissão) — então só aqui faz sentido barrar "Gerar fatura" antes de
  // criar. Reaproveita o MESMO endpoint/validador do backend (GET
  // /clients/{id}/cobranca-requisitos — nunca uma lista de regras
  // duplicada no frontend). Falha ao CONSULTAR (rede/backend fora do ar)
  // nunca trava a criação — a emissão real continua sendo a defesa
  // final. Últimos ajustes antes da produção, item 2 — agora CIENTE da
  // modalidade escolhida (pix_modalidade/inclui_boleto), nunca exige
  // CPF/CNPJ do cliente quando a composição é 100% PIX por chave.
  const ultimaValidacaoRef = useRef({});
  const validarCadastroCliente = async ({ pixModalidade: pm, incluiBoleto } = {}) => {
    ultimaValidacaoRef.current = { pixModalidade: pm, incluiBoleto };
    try {
      const params = {};
      if (pm) params.pix_modalidade = pm;
      if (incluiBoleto) params.inclui_boleto = true;
      const { data } = await api.get(`/clients/${clienteId}/cobranca-requisitos`, { params });
      if (!data.ok) {
        avisarCadastroIncompleto(data.mensagem || "Cadastro do cliente incompleto para emitir cobrança.");
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  // "Editar cliente" abre o diálogo de cadastro POR CIMA deste modal — nunca
  // mais navega para a tela de Clientes (era o que fazia o usuário perder
  // todo o plano de pagamento já montado).
  const avisarCadastroIncompleto = (mensagem) => {
    setCadastroPendente({ mensagem });
    toast.error(mensagem, { duration: 8000 });
  };

  // Depois do save: revalida com os MESMOS parâmetros da tentativa que
  // falhou. Resolvido -> o aviso sai e o usuário clica "Gerar fatura" de
  // novo (nunca emitimos sozinhos). Ainda faltando -> o próximo campo é
  // informado e o diálogo pode ser reaberto.
  const onClienteSalvo = async () => {
    const ok = await validarCadastroCliente(ultimaValidacaoRef.current || {});
    if (ok) {
      setCadastroPendente(null);
      toast.success("Cadastro completo. Clique em \"Gerar fatura\" para emitir.");
    }
  };

  const submit = async () => {
    setSaving(true);
    // misto sempre tem boleto (comp2); entrada em PIX e/ou parcelamento em
    // PIX são os outros casos de instrumento escolhido NA CRIAÇÃO
    // (item 2/cenário A) — os dois podem coexistir com modalidades
    // diferentes (ex.: entrada boleto + parcelamento PIX por chave).
    const entradaPix = entradaAtiva && entradaInstrumento === "pix";
    const parcelamentoPix = forma === "parcelado" && parcelamentoInstrumento === "pix";
    const precisaValidarCadastro = misto || entradaPix || parcelamentoPix;
    if (precisaValidarCadastro) {
      const algumBoleto = misto
        || (entradaAtiva && entradaInstrumento === "boleto")
        || (forma === "parcelado" && parcelamentoInstrumento === "boleto");
      const algumPixIntegrado = (misto && pixModalidade === "integrado")
        || (entradaPix && entradaPixModalidade === "integrado")
        || (parcelamentoPix && parcelamentoPixModalidade === "integrado");
      const algumPixChave = (misto && pixModalidade === "chave")
        || (entradaPix && entradaPixModalidade === "chave")
        || (parcelamentoPix && parcelamentoPixModalidade === "chave");
      const pm = misto ? pixModalidade : (algumPixChave ? "chave" : (algumPixIntegrado ? "integrado" : undefined));
      const ok = await validarCadastroCliente({ pixModalidade: pm, incluiBoleto: algumBoleto || algumPixIntegrado });
      if (!ok) { setSaving(false); return; }
    }
    try {
      if (misto) {
        const boletoComponente = { instrumento_tipo: "boleto", valor: (boletoCents / 100).toFixed(2) };
        if (boletoParcelado) {
          if (modoParcelamento === "quantidade") {
            boletoComponente.modo_parcelamento = "quantidade";
            boletoComponente.parcelas = vencs.length;
            boletoComponente.primeiro_vencimento = vencs[0];
            boletoComponente.vencimentos = vencs;
          } else if (repetirValor === true) {
            boletoComponente.modo_parcelamento = "valor_base";
            boletoComponente.parcela_valor_base = primeiraParcela.trim();
            boletoComponente.primeiro_vencimento = primeiro;
            boletoComponente.vencimentos = vencsValorBase;
          } else if (repetirValor === false) {
            boletoComponente.modo_parcelamento = "manual";
            boletoComponente.parcelas_valores = parcelasManuaisLinhas.map(r => r.valor.trim());
            boletoComponente.vencimentos = parcelasManuaisLinhas.map(r => r.vencimento);
          }
        } else {
          boletoComponente.primeiro_vencimento = boletoVencimentoUnico;
        }
        // Item 5 do pedido — nunca envia component_id: quem atribui
        // comp1(PIX)/comp2(BOLETO) é sempre o backend.
        const body = {
          cliente_id: clienteId,
          ticket_ids: tickets.map(t => t.id),
          forma_pagamento: "misto",
          componentes: [
            { instrumento_tipo: "pix", valor: pixValor.trim(), primeiro_vencimento: pixVencimento, pix_modalidade: pixModalidade },
            boletoComponente,
          ],
        };
        const { data } = await api.post("/invoices", body);
        toast.success(`Fatura ${data.numero} criada (pagamento misto). Emita PIX e boleto separadamente em "Emitir parcelas".`);
        onOpenChange(false);
        onCreated?.();
        return;
      }
      const body = {
        cliente_id: clienteId,
        ticket_ids: tickets.map(t => t.id),
        forma_pagamento: forma,
      };
      let totalParcelas = 1;
      if (entradaAtiva) {
        body.possui_entrada = true;
        body.entrada_valor = entradaValor.trim();
        body.entrada_vencimento = entradaVencimento;
        body.entrada_instrumento_tipo = entradaInstrumento;
        if (entradaInstrumento === "pix") body.entrada_pix_modalidade = entradaPixModalidade;
      }
      if (forma === "parcelado") {
        body.parcelamento_instrumento_tipo = parcelamentoInstrumento;
        if (parcelamentoInstrumento === "pix") body.parcelamento_pix_modalidade = parcelamentoPixModalidade;
        if (modoParcelamento === "quantidade") {
          body.modo_parcelamento = "quantidade";
          body.parcelas = vencs.length;
          body.primeiro_vencimento = vencs[0];
          body.vencimentos = vencs;
          totalParcelas = vencs.length;
        } else if (repetirValor === true) {
          body.modo_parcelamento = "valor_base";
          // Etapa 2.1 — string exata (não Number), o backend valida
          // Decimal com no máximo 2 casas e converte para centavos.
          body.parcela_valor_base = primeiraParcela.trim();
          body.primeiro_vencimento = primeiro;
          body.vencimentos = vencsValorBase;
          totalParcelas = valorBasePreview.length;
        } else if (repetirValor === false) {
          body.modo_parcelamento = "manual";
          body.parcelas_valores = parcelasManuaisLinhas.map(r => r.valor.trim());
          body.vencimentos = parcelasManuaisLinhas.map(r => r.vencimento);
          totalParcelas = parcelasManuaisCents.length;
        }
      }
      const { data } = await api.post("/invoices", body);
      if (forma === "parcelado") {
        const entradaTxt = entradaAtiva ? "entrada + " : "";
        toast.success(`Fatura ${data.numero} criada com ${entradaTxt}${totalParcelas} parcelas. Emita as cobranças em "Emitir parcelas".`);
      } else {
        toast.success(`Fatura ${data.numero} criada (à vista).`);
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      // A emissão real é a defesa final: quando ELA aponta dado faltante do
      // cadastro, o atalho "Editar cliente" também aparece aqui — o mesmo
      // caminho da validação prévia, sem perder o plano de pagamento.
      const msg = formatApiError(e);
      if (camposFaltantesDaMensagem(msg).length) avisarCadastroIncompleto(msg);
      else toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Editor de parcelamento (quantidade/valor-base/manual) — usado pelo
  // saldo do fluxo único (entrada+parcelado) E pelo componente BOLETO do
  // misto (item 3 do pedido: "reutilizar os controles existentes, não
  // duplicar lógica financeira"). Fecha sobre o estado do componente —
  // nunca recebe props — por isso só é renderizado num único lugar por
  // vez (fluxo único OU misto, nunca os dois).
  //
  // Item 7 do pacote Faturamento/PIX/Fichas — dividido em DOIS blocos:
  // os CONTROLES (modo/quantidade/valor-base — ficam na coluna esquerda,
  // junto do resto do formulário) e as LINHAS por parcela (número/valor/
  // vencimento — mudam para a coluna direita, no espaço que antes era só
  // do Resumo, para maximizar a área visível sem rolagem interna).
  const modoParcelamentoControles = (
    <>
      <div>
        <Label className="text-sm">Modo de parcelamento{entradaAtiva ? " do saldo" : misto ? " do boleto" : ""}</Label>
        <div className="flex gap-2 mt-1.5">
          <Button type="button" size="sm" variant={modoParcelamento === "quantidade" ? "default" : "outline"}
            onClick={() => setModoParcelamento("quantidade")} data-testid="modo-quantidade">Dividir igualmente</Button>
          <Button type="button" size="sm" variant={modoParcelamento === "valor" ? "default" : "outline"}
            onClick={() => setModoParcelamento("valor")} data-testid="modo-valor">Informar valor das parcelas</Button>
        </div>
      </div>

      {modoParcelamento === "quantidade" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Parcelas</Label>
            <Input type="number" min="2" max={MAX_PARCELAS} value={parcelas}
              onChange={e => setParcelas(e.target.value)} data-testid="aglut-parcelas" />
          </div>
          <div>
            <Label className="text-xs">1º vencimento</Label>
            <Input type="date" value={primeiro} onChange={e => setPrimeiro(e.target.value)} data-testid="aglut-primeiro-venc" />
          </div>
        </div>
      )}

      {modoParcelamento === "valor" && (
        <>
          {/* Item 6 — "valor da primeira parcela" só faz sentido no Caso A
              (valor-base repete esse valor); o Caso B (Não) pergunta a
              quantidade de parcelas e cada linha tem seu próprio valor. */}
          {repetirValor !== false && (
            <div>
              <Label className="text-xs">Valor da primeira parcela</Label>
              <Input type="number" step="0.01" min="0.01" value={primeiraParcela}
                onChange={e => setPrimeiraParcela(e.target.value)} data-testid="valor-primeira-parcela" />
            </div>
          )}
          <div>
            <Label className="text-sm">Repetir este valor nas próximas parcelas enquanto houver saldo?</Label>
            <div className="flex gap-2 mt-1.5">
              <Button type="button" size="sm" variant={repetirValor === true ? "default" : "outline"}
                onClick={() => setRepetirValor(true)} data-testid="repetir-sim">Sim</Button>
              <Button type="button" size="sm" variant={repetirValor === false ? "default" : "outline"}
                onClick={() => setRepetirValor(false)} data-testid="repetir-nao">Não</Button>
            </div>
          </div>

          {repetirValor === true && (
            <div>
              <Label className="text-xs">1º vencimento</Label>
              <Input type="date" value={primeiro} onChange={e => setPrimeiro(e.target.value)} data-testid="aglut-primeiro-venc" />
              <p className="text-xs text-slate-500 mt-1">As demais datas são sugeridas mensalmente a partir desta — ajuste cada uma ao lado, se precisar.</p>
            </div>
          )}

          {repetirValor === false && (
            <div>
              <Label className="text-xs">Quantidade de parcelas</Label>
              <Input type="number" min="2" max={MAX_PARCELAS} value={parcelasManuaisQtd}
                onChange={e => setParcelasManuaisQtd(e.target.value)} data-testid="parcelas-manuais-qtd" />
              <p className="text-xs text-slate-500 mt-1">Informe o valor e o vencimento de cada parcela ao lado.</p>
            </div>
          )}
        </>
      )}
    </>
  );

  // Item 6/7 — as LINHAS por parcela (renderizadas na coluna direita).
  const modoParcelamentoLinhas = (
    <>
      {modoParcelamento === "quantidade" && (
        <>
          <div className="text-xs text-slate-500 mb-1">Parcelas (o servidor recalcula os valores em centavos — a soma é sempre exata):</div>
          <div className="space-y-1">
            {vencs.map((v, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`parcela-row-${i + 1}`}>
                <span className="w-10 text-slate-500">{i + 1}/{vencs.length}</span>
                <span className="w-24 font-medium">{money((valoresPreview[i] || 0) / 100)}</span>
                <Input type="date" value={v} className="flex-1"
                  onChange={e => setVencs(vs => vs.map((x, j) => j === i ? e.target.value : x))}
                  data-testid={`parcela-venc-${i + 1}`} />
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Soma das parcelas: {money(valoresPreview.reduce((a, c) => a + c, 0) / 100)} · Vencimentos que não existirem no mês usam o último dia válido.
          </div>
        </>
      )}

      {modoParcelamento === "valor" && repetirValor === true && (
        <>
          <div className="text-xs text-slate-500 mb-1">Parcelas — vencimento individual (o servidor recalcula os valores — a soma é sempre exata):</div>
          {primeiraParcelaCents > parcelamentoBaseCents ? (
            <p className="text-xs text-red-600">O valor da parcela não pode ser maior que o total a parcelar.</p>
          ) : (
            <div className="space-y-1" data-testid="valor-base-preview">
              {valorBasePreview.map((v, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`valor-base-row-${i + 1}`}>
                  <span className="w-10 text-slate-500">{i + 1}/{valorBasePreview.length}</span>
                  <span className="w-24 font-medium">{money(v / 100)}</span>
                  <Input type="date" value={vencsValorBase[i] || ""} className="flex-1"
                    onChange={e => setVencsValorBase(vs => vs.map((x, j) => j === i ? e.target.value : x))}
                    data-testid={`valor-base-venc-${i + 1}`} />
                </div>
              ))}
            </div>
          )}
          <div className="text-xs text-slate-400 mt-1">
            Total das parcelas: {money(valorBasePreview.reduce((a, c) => a + c, 0) / 100)}
          </div>
        </>
      )}

      {modoParcelamento === "valor" && repetirValor === false && (
        <div className="space-y-2">
          <div className="text-xs text-slate-500 mb-1">Valor e vencimento de cada parcela:</div>
          <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1">
            <div><span className="text-slate-500">Distribuído: </span><b>{money(distribuidoCents / 100)}</b></div>
            <div>
              <span className="text-slate-500">Restante: </span>
              <b className={restanteCents === 0 ? "text-emerald-600" : "text-amber-600"} data-testid="resumo-restante">
                {money(restanteCents / 100)}
              </b>
            </div>
          </div>
          <div className="space-y-1">
            {parcelasManuaisLinhas.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`parcela-manual-row-${i + 1}`}>
                <span className="w-6 text-slate-500">{i + 1}</span>
                <Input type="number" step="0.01" min="0.01" value={r.valor} placeholder="Valor" className="w-28"
                  onChange={e => setParcelaManualCampo(i, "valor", e.target.value)}
                  data-testid={`parcela-manual-valor-${i + 1}`} />
                <Input type="date" value={r.vencimento} className="flex-1"
                  onChange={e => setParcelaManualCampo(i, "vencimento", e.target.value)}
                  data-testid={`parcela-manual-venc-${i + 1}`} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Item 3 do pedido (ajustes finais) — layout unificado em 2 colunas
          (config à esquerda / resumo sempre visível à direita) para os
          dois fluxos, dialog mais largo para caber sem rolagem num
          desktop comum. Fecha só por X/Cancelar/ação bem-sucedida/Esc —
          clique fora NUNCA fecha (onPointerDownOutside). */}
      <DialogContent className="max-w-5xl" onPointerDownOutside={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers size={18} className="text-primary" />
            {tickets.length >= 2 ? "Aglutinar fichas em uma fatura" : "Forma de pagamento do faturamento"}
          </DialogTitle>
        </DialogHeader>

        {/* Correção visual final — Modal de parcelamento, item 1: Cliente/
            Fichas/Total (e antes disso, "Composição do pagamento") ainda
            ficavam FORA/ACIMA do grid de 2 colunas, como blocos full-width
            — cada um empurrando a coluna direita mais uma linha pra baixo,
            o espaço vazio visto em homologação. O grid de 2 colunas agora
            começa IMEDIATAMENTE após o cabeçalho; tudo isso vira o TOPO da
            coluna esquerda, e a coluna direita (editor/resumo) começa na
            MESMA linha do grid, alinhada com "Cliente" (align-items:start
            — nunca margem negativa/position absolute/transform/hack). */}
        <div className="max-h-[88vh] overflow-y-auto">
          <div className="grid md:grid-cols-[1.3fr_1fr] gap-4 items-start" data-testid="aglut-grid-2col">
            {/* Esquerda — resumo do cliente/composição/configuração das parcelas */}
            <div className="space-y-4" data-testid="aglut-coluna-esquerda">
              <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1">
                <div><span className="text-slate-500">Cliente: </span><b>{clienteNome}</b></div>
                <div><span className="text-slate-500">Fichas selecionadas: </span><b data-testid="aglut-qtd">{tickets.length}</b></div>
                <div><span className="text-slate-500">Total: </span><b className="text-primary font-heading">{money(total)}</b></div>
              </div>
              <div>
                <Label className="text-sm">Composição do pagamento</Label>
                <div className="flex gap-2 mt-1.5">
                  <Button type="button" size="sm" variant={!misto ? "default" : "outline"}
                    onClick={() => setTipoComposicao("unica")} data-testid="composicao-unica">Uma forma</Button>
                  <Button type="button" size="sm" variant={misto ? "default" : "outline"}
                    onClick={() => setTipoComposicao("misto")} data-testid="composicao-misto">Pagamento misto</Button>
                </div>
              </div>
              {!misto && (
                <>
                  <div>
                    <Label className="text-sm">Forma de pagamento</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Button type="button" size="sm" variant={forma === "avista" ? "default" : "outline"}
                        onClick={() => setForma("avista")} data-testid="forma-avista">À vista</Button>
                      <Button type="button" size="sm" variant={forma === "parcelado" ? "default" : "outline"}
                        onClick={() => setForma("parcelado")} data-testid="forma-parcelado">Parcelado</Button>
                    </div>
                  </div>

                  {forma === "avista" && (
                    <div>
                      <Label className="text-xs">Vencimento</Label>
                      <Input type="date" value={venc1} onChange={e => setVenc1(e.target.value)} data-testid="aglut-venc-avista" />
                      <p className="text-xs text-slate-500 mt-1">A fatura é criada agora; a cobrança (boleto/PIX) é emitida depois, pelo botão "Gerar boleto".</p>
                    </div>
                  )}

                  {forma === "parcelado" && (
                    <>
                      <div>
                        <Label className="text-sm">Possui entrada?</Label>
                        <div className="flex gap-2 mt-1.5">
                          <Button type="button" size="sm" variant={!possuiEntrada ? "default" : "outline"}
                            onClick={() => setPossuiEntrada(false)} data-testid="entrada-nao">Não</Button>
                          <Button type="button" size="sm" variant={possuiEntrada ? "default" : "outline"}
                            onClick={() => setPossuiEntrada(true)} data-testid="entrada-sim">Sim</Button>
                        </div>
                      </div>

                      {entradaAtiva && (
                        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Valor da entrada</Label>
                              <Input type="number" step="0.01" min="0.01" value={entradaValor}
                                onChange={e => setEntradaValor(e.target.value)} data-testid="entrada-valor" />
                            </div>
                            <div>
                              <Label className="text-xs">Vencimento da entrada</Label>
                              <Input type="date" value={entradaVencimento}
                                onChange={e => setEntradaVencimento(e.target.value)} data-testid="entrada-vencimento" />
                            </div>
                          </div>
                          {!entradaValida && entradaCents > 0 && (
                            <p className="text-xs text-red-600">
                              A entrada não pode ser maior ou igual ao valor total do faturamento — use a cobrança à vista nesse caso.
                            </p>
                          )}
                          {/* Últimos ajustes antes da produção, item 2 — instrumento
                              da entrada (Boleto, padrão histórico, ou PIX); só
                              oferece PIX quando alguma modalidade está configurada. */}
                          {(temPixIntegrado || temPixChave) && (
                            <div>
                              <Label className="text-xs">Instrumento da entrada</Label>
                              <div className="flex gap-2 mt-1.5">
                                <Button type="button" size="sm" variant={entradaInstrumento === "boleto" ? "default" : "outline"}
                                  onClick={() => { setEntradaInstrumento("boleto"); setEntradaPixModalidade(null); }} data-testid="entrada-instrumento-boleto">Boleto</Button>
                                <Button type="button" size="sm" variant={entradaInstrumento === "pix" ? "default" : "outline"}
                                  onClick={() => setEntradaInstrumento("pix")} data-testid="entrada-instrumento-pix">PIX</Button>
                              </div>
                            </div>
                          )}
                          {entradaInstrumento === "pix" && temPixIntegrado && temPixChave && (
                            <div>
                              <Label className="text-xs">Modalidade do PIX da entrada</Label>
                              <div className="flex gap-2 mt-1.5">
                                <Button type="button" size="sm" variant={entradaPixModalidade === "integrado" ? "default" : "outline"}
                                  onClick={() => setEntradaPixModalidade("integrado")} data-testid="entrada-pix-modalidade-integrado">PIX integrado</Button>
                                <Button type="button" size="sm" variant={entradaPixModalidade === "chave" ? "default" : "outline"}
                                  onClick={() => setEntradaPixModalidade("chave")} data-testid="entrada-pix-modalidade-chave">PIX por chave</Button>
                              </div>
                            </div>
                          )}
                          <div className="text-xs text-slate-500">
                            Saldo a parcelar: <b className="text-slate-700" data-testid="saldo-a-parcelar">{money(baseParcelamentoCents / 100)}</b>
                          </div>
                        </div>
                      )}

                      {/* Delta Parcelamento via PIX — "Forma da cobrança" do
                          parcelamento (independente do instrumento da
                          entrada acima). Boleto é o padrão histórico. */}
                      <div>
                        <Label className="text-sm">Forma da cobrança</Label>
                        <div className="flex gap-2 mt-1.5">
                          <Button type="button" size="sm" variant={parcelamentoInstrumento === "boleto" ? "default" : "outline"}
                            onClick={() => { setParcelamentoInstrumento("boleto"); setParcelamentoPixModalidade(null); }} data-testid="parcelamento-instrumento-boleto">Boleto</Button>
                          {(temPixIntegrado || temPixChave) && (
                            <Button type="button" size="sm" variant={parcelamentoInstrumento === "pix" ? "default" : "outline"}
                              onClick={() => setParcelamentoInstrumento("pix")} data-testid="parcelamento-instrumento-pix">PIX</Button>
                          )}
                        </div>
                        {parcelamentoInstrumento === "pix" && (
                          <p className="text-xs text-slate-500 mt-1">Cada parcela vira uma cobrança PIX com vencimento próprio — nunca um financiamento/cartão.</p>
                        )}
                      </div>
                      {parcelamentoInstrumento === "pix" && temPixIntegrado && temPixChave && (
                        <div>
                          <Label className="text-xs">Modalidade do PIX parcelado</Label>
                          <div className="flex gap-2 mt-1.5">
                            <Button type="button" size="sm" variant={parcelamentoPixModalidade === "integrado" ? "default" : "outline"}
                              onClick={() => setParcelamentoPixModalidade("integrado")} data-testid="parcelamento-pix-modalidade-integrado">PIX integrado</Button>
                            <Button type="button" size="sm" variant={parcelamentoPixModalidade === "chave" ? "default" : "outline"}
                              onClick={() => setParcelamentoPixModalidade("chave")} data-testid="parcelamento-pix-modalidade-chave">PIX por chave</Button>
                          </div>
                        </div>
                      )}

                      {modoParcelamentoControles}
                    </>
                  )}
                </>
              )}

              {misto && (
                <>
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="font-medium text-sm text-slate-700">PIX</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Valor</Label>
                        <Input type="number" step="0.01" min="0.01" value={pixValor}
                          onChange={e => setPixValor(e.target.value)} data-testid="misto-pix-valor" />
                      </div>
                      <div>
                        <Label className="text-xs">Vencimento</Label>
                        <Input type="date" value={pixVencimento} onChange={e => setPixVencimento(e.target.value)} data-testid="misto-pix-vencimento" />
                      </div>
                    </div>
                    {/* Últimos ajustes antes da produção, item 2 — modalidade do
                        componente PIX do misto (sempre PIX — a escolha aqui é
                        só integrado x chave). */}
                    {temPixIntegrado && temPixChave ? (
                      <div>
                        <Label className="text-xs">Modalidade</Label>
                        <div className="flex gap-2 mt-1.5">
                          <Button type="button" size="sm" variant={pixModalidade === "integrado" ? "default" : "outline"}
                            onClick={() => setPixModalidade("integrado")} data-testid="misto-pix-modalidade-integrado">PIX integrado</Button>
                          <Button type="button" size="sm" variant={pixModalidade === "chave" ? "default" : "outline"}
                            onClick={() => setPixModalidade("chave")} data-testid="misto-pix-modalidade-chave">PIX por chave</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">
                        {pixModalidade === "chave" ? "PIX por chave (única modalidade configurada)." : pixModalidade === "integrado" ? "PIX integrado (única modalidade configurada)." : "Nenhuma modalidade de PIX configurada — configure em Configurações → Bancos e Pagamentos."}
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm text-slate-700">Boleto</div>
                      <span className="text-sm font-medium" data-testid="misto-boleto-valor">{money(boletoCents / 100)}</span>
                    </div>
                    <p className="text-xs text-slate-500">Valor restante calculado automaticamente (total − PIX).</p>

                    <div>
                      <Label className="text-sm">Parcelar boleto?</Label>
                      <div className="flex gap-2 mt-1.5">
                        <Button type="button" size="sm" variant={boletoParcelado === false ? "default" : "outline"}
                          onClick={() => setBoletoParcelado(false)} data-testid="boleto-parcelar-nao">Não</Button>
                        <Button type="button" size="sm" variant={boletoParcelado === true ? "default" : "outline"}
                          onClick={() => setBoletoParcelado(true)} data-testid="boleto-parcelar-sim">Sim</Button>
                      </div>
                    </div>

                    {boletoParcelado === false && (
                      <div>
                        <Label className="text-xs">Vencimento</Label>
                        <Input type="date" value={boletoVencimentoUnico}
                          onChange={e => setBoletoVencimentoUnico(e.target.value)} data-testid="misto-boleto-vencimento-unico" />
                      </div>
                    )}

                    {boletoParcelado === true && modoParcelamentoControles}
                  </div>
                </>
              )}
            </div>

            {/* Direita — item 7 do pacote Faturamento/PIX/Fichas: agora é o
                espaço PRINCIPAL das parcelas (número/valor/vencimento
                editáveis, maximizando a área visível sem rolagem interna
                numa resolução normal) + o Resumo compacto (item 8) logo
                abaixo, sem repetir cada parcela de novo. */}
            <div className="space-y-3 md:sticky md:top-0" data-testid="aglut-coluna-direita">
              {parcelamentoAtivo && (
                <div className="rounded-lg border border-slate-200 p-3" data-testid="parcelas-editor">
                  {modoParcelamentoLinhas}
                </div>
              )}

              <div className="rounded-lg bg-slate-50 p-4 text-sm space-y-1.5" data-testid="resumo-painel">
                <div className="font-medium text-slate-700 mb-1">Resumo</div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Valor faturado</span><b>{money(total)}</b>
                </div>

                {!misto && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Forma</span>
                      <b data-testid="resumo-forma">
                        {forma === "avista" ? "À vista" : `Parcelado${entradaAtiva ? " + entrada" : ""} · ${parcelamentoInstrumento === "pix" ? "PIX" : "Boleto"}`}
                      </b>
                    </div>
                    {forma === "parcelado" && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Parcelamento</span>
                        <b data-testid="resumo-parcelamento">{qtdParcelasSaldo}x</b>
                      </div>
                    )}
                    {entradaAtiva && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Entrada</span>
                        <b>{money(entradaCents / 100)} — {fmtDate(entradaVencimento)}</b>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">1º vencimento</span>
                      <b data-testid="resumo-primeiro-vencimento">
                        {fmtDate(forma === "avista" ? venc1 : (entradaAtiva ? entradaVencimento : primeiraDataSaldo))}
                      </b>
                    </div>
                    {forma === "parcelado" && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Total da composição</span>
                          <b>{money(composicaoTotalCents / 100)}</b>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Diferença</span>
                          <b className={composicaoDiferencaCents === 0 ? "text-emerald-600" : "text-red-600"} data-testid="resumo-diferenca">
                            {money(composicaoDiferencaCents / 100)}
                          </b>
                        </div>
                      </>
                    )}
                  </>
                )}

                {misto && (
                  <div data-testid="resumo-misto">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Forma</span><b>Misto — PIX + Boleto</b>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">PIX</span><b>{money(pixCents / 100)} — {fmtDate(pixVencimento)}</b>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Boleto</span>
                      <b>
                        {money(boletoCents / 100)}
                        {boletoParcelado === true ? ` — ${qtdParcelasSaldo}x` : boletoParcelado === false ? " — à vista" : ""}
                      </b>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">1º vencimento (boleto)</span>
                      <b>{fmtDate(boletoParcelado === true ? primeiraDataSaldo : boletoVencimentoUnico)}</b>
                    </div>
                    <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between">
                      <span className="text-slate-500">Total distribuído</span>
                      <b>{money(mistoSomaCents / 100)}</b>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Diferença</span>
                      <b className={mistoDiferencaCents === 0 ? "text-emerald-600" : "text-red-600"} data-testid="resumo-misto-diferenca">
                        {money(mistoDiferencaCents / 100)}
                      </b>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {cadastroPendente && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3"
               data-testid="aglut-cadastro-pendente">
            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-700" />
            <div className="flex-1 text-xs text-amber-800">
              <div className="font-medium">Não foi possível emitir a cobrança.</div>
              <div>{cadastroPendente.mensagem}</div>
            </div>
            <Button size="sm" variant="outline" className="shrink-0"
              onClick={() => setClienteEdit({ mensagem: cadastroPendente.mensagem })}
              data-testid="aglut-editar-cliente">
              Editar cliente
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={submit}
            disabled={saving || tickets.length < 1 || (misto ? !mistoPlanoValido : !planoValido)} data-testid="aglut-gerar">
            {saving ? "Gerando..." : "Gerar fatura"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Montado como IRMÃO do DialogContent: este modal permanece aberto e
          montado enquanto o cadastro é corrigido. */}
      <ClienteEditDialog
        open={!!clienteEdit}
        clienteId={clienteId}
        mensagem={clienteEdit?.mensagem || ""}
        onOpenChange={(v) => !v && setClienteEdit(null)}
        onSaved={onClienteSalvo}
      />
    </Dialog>
  );
}
